import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { filesAPIService } from '~/api/files';
import { pdfMicrositeAPIService } from '~/api/microsites/pdf';
import DeleteIcon from '~/assets/icons/svgs/trash-bin.svg?react';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import PDFPreviewIcon from '~/components/ui/PDFPreviewIcon';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useAppDispatch } from '~/hooks/useRedux';
import { getIsMobile } from '~/store/reducers/app/selectors';
import { updatePdfFileThunk } from '~/store/reducers/previewScreens/pdf';
import { getPdfReducer } from '~/store/reducers/previewScreens/pdf/selectors';
import { cutString, isMaxLength } from '~/utils/common';

import { useStyles } from './ImageItem.styled';

export const ImageItem = ({ index, item, actions, images }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isMobile = useSelector(getIsMobile);
  const { micrositeData, basicInformation } = useSelector(getPdfReducer);

  const styles = useStyles();

  const addNewImageToPdf = async (event, index) => {
    const file = event.target.files[0];
    const url = window.URL.createObjectURL(file);
    const storageResponse = await filesAPIService.uploadImage(file);

    dispatch(actions.setPdfItemDetails({ index, value: url, field: 'previewLink' }));
    dispatch(actions.setPdfItemDetails({ index, value: storageResponse.data, field: 'imageStorageId' }));

    dispatch(
      updatePdfFileThunk({
        id: micrositeData.id,
        body: {
          id: item?.unique_id,
          data: { preview_image_id: storageResponse.data },
        },
        shouldUpdate: true,
      }),
    );
  };

  const removeIconFromPdf = (index) => {
    dispatch(actions.setPdfItemDetails({ index, value: '', field: 'previewLink' }));

    dispatch(
      updatePdfFileThunk({
        id: micrositeData.id,
        body: {
          id: item?.unique_id,
          data: { preview_image_id: null },
        },
        shouldUpdate: true,
      }),
    );
  };

  const handleRemoveImage = async (item, activeIndex) => {
    await pdfMicrositeAPIService.deletePdfFiles(item.unique_id);
    dispatch(actions.setImages(images.filter((item, index) => index !== activeIndex)));
    const updatedResponse = await pdfMicrositeAPIService.getPdfSite(micrositeData.id);
    dispatch(actions.setMicrositeData(updatedResponse.data));
  };

  const handleChangeItemValues = useDebouncedCallback((e, field) => {
    const {
      target: { value },
    } = e;
    dispatch(actions.setPdfItemDetails({ field, value, index }));
  }, 100);

  const sendPdfField = async ({ item, index, field }) => {
    if (micrositeData.id) {
      dispatch(
        updatePdfFileThunk({
          id: micrositeData.id,
          body: {
            id: item.unique_id,
            data: { [field]: basicInformation.images[index][field] },
          },
          shouldUpdate: true,
        }),
      );
    }
  };

  return (
    <Draggable draggableId={item?.id} index={index}>
      {(provided) => (
        <Box ref={provided.innerRef} {...provided.draggableProps} sx={styles.container}>
          <Box sx={styles.header}>
            <Box sx={styles.info}>
              <PDFPreviewIcon />
              <Box>
                <Text variant="t16Regular">{cutString(item?.name ?? item?.path, 0, isMobile ? 20 : 40)}</Text>
                <Text variant="inputSmall" sx={styles.description}>
                  {item?.size}
                </Text>
              </Box>
            </Box>

            <Box sx={styles.icons}>
              <IconButton disableRipple {...provided.dragHandleProps}>
                <MenuIcon />
              </IconButton>
              <IconButton disableRipple onClick={() => handleRemoveImage(item, index)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          <Text variant="inputSmall" sx={styles.imageTitle}>
            {t('template5:image')}
          </Text>
          <ImageUploader
            image={item?.previewLink}
            onDeleteImage={() => removeIconFromPdf(index, item)}
            onUploadImage={(e) => addNewImageToPdf(e, index, item)}
          />
          <TextInput
            labelSx={{ marginTop: '12px' }}
            fullWidth
            label={t('labels:name')}
            defaultValue={item?.name}
            onBlur={() => sendPdfField({ item, index, field: 'name' })}
            onChange={(e) => handleChangeItemValues(e, 'name')}
            inputProps={{ maxLength: 100 }}
            helperText={(!item.name && t('inputs:required')) || isMaxLength(item?.name, 100)}
            error={!item.name}
            placeholder={t('common:enterName')}
          />
          <TextInput
            sx={styles.input}
            fullWidth
            placeholder={t('common:enterDescription')}
            defaultValue={item?.description}
            onBlur={() => sendPdfField({ item, index, field: 'description' })}
            onChange={(e) => handleChangeItemValues(e, 'description')}
            inputProps={{ maxLength: 200 }}
            helperText={isMaxLength(item?.description, 200)}
          />
        </Box>
      )}
    </Draggable>
  );
};
